/**
 * @fileoverview Prompt template for world-system-details-generator-v3.
 *
 * Supports multimodal input with optional reference images.
 */

export const prompt = `
You are an expert world-builder specializing in designing coherent, internally consistent systems — the underlying rules, forces, and structures that govern how fictional worlds operate.

## TASK INSTRUCTIONS:
Your task is to generate complete details for the specified world system, following the selected creative direction.

1. Review the system's basic information and the selected creative direction.
{{#if hasImages}}
2. Use the provided reference images to inform visual details, atmosphere, and styling.
3. Generate detailed content for each attribute field listed below.
{{else}}
2. Generate detailed content for each attribute field listed below.
{{/if}}
{{#if hasImages}}4{{else}}3{{/if}}. Ensure all details:
   - Are consistent with the selected creative direction
   - Fit naturally within the story's lore and setting
   - Have clear internal logic — rules should be consistent and consequences should follow logically
   - Create meaningful constraints and trade-offs (no system should be all-powerful or consequence-free)
   - Consider how this system interacts with other known world systems
   - Include specific examples of how the system manifests in practice
{{#if hasImages}}
   - Incorporate visual elements from the reference images where appropriate
{{/if}}

{{#if hasImages}}
## IMAGE VALIDATION

Before using the reference images, perform a quick validation:
- The images should contain visual content (photos, artwork, illustrations, etc.)
- Images that are primarily text (documents, screenshots of text, etc.) are NOT valid visual references

If the images are NOT valid visual references:
- Set \`isValidImage\` to \`false\`
- Provide a brief \`validationError\` message explaining why
- Do NOT provide \`attributes\`

If the images ARE valid:
- Set \`isValidImage\` to \`true\`
- Use them as visual inspiration for the system's details
- Provide the \`attributes\` object with generated content

## REFERENCE IMAGES

The provided images should be used as visual references for this system. Extract relevant details such as:
- Visual manifestation of the system (glowing runes, technological interfaces, etc.)
- Atmosphere, mood, and tone
- Color palette and visual themes
- Environmental context
- Any distinctive visual characteristics
{{/if}}

<<<userMessage>>>

## SYSTEM TO DEVELOP

<<<activeWorldSystem>>>

## SELECTED CREATIVE DIRECTION

{{{json selectedIdea}}}

## STORY CONTEXT

<<<loreContext>>>

<<<storyContext>>>

<<<characters>>>

## OUTPUT INSTRUCTIONS:

{{#if hasImages}}
Your output MUST include:
- \`isValidImage\`: boolean - true if reference images are valid visual content, false if they're just text
- \`validationError\`: string (only if isValidImage is false) - brief explanation of why images are invalid
- \`attributes\`: object (only if isValidImage is true) - the generated system details
{{else}}
Your output MUST include:
- \`isValidImage\`: true (no images to validate)
- \`attributes\`: object - the generated system details
{{/if}}

For the \`attributes\` object, generate content for each of the following fields:

{{#each systemFields}}
- **{{this.label}}** (key: {{this.key}}): {{{this.description}}}
{{/each}}

Provide your output in the required JSON format.
`;
